import { apiRequest, type ApiProblem } from './httpRequest';
import type { ChemMoleculeV1Document } from '@peruselab/structure-editor/domain/moleculeProjection';

// ADR 0026 Q3 (#1161): SAME_SKELETON -- a structure with a metal, the same atoms and drawn bonds
// as the query but other charges, radicals or hapto bonds (ferrocene with hapto bonds vs as ions).
export type MatchCategory = 'IDENTICAL' | 'SAME_SKELETON' | 'SUBSTRUCTURE' | 'SUPERSTRUCTURE' | 'SIMILAR';

/** #711, #1161: results rank Identical first, then Same skeleton (its score can be low), then by
 *  score -- the server's StructureMatch ranking. Lower is better. */
export function matchRank(category: string): number {
  return category === 'IDENTICAL' ? 0 : category === 'SAME_SKELETON' ? 1 : 2;
}

// Which slot of a Reaction the query molecule must occupy for the reaction to count as a
// result (ADR 0008). 'ANY' imposes no role filter and is never sent to the server -- it exists
// only as a legacy/internal sentinel; the wire format is a set of the three real categories
// below (#563).
export type ReactionBucket = 'ANY' | 'REACTANT' | 'PRODUCT' | 'CONDITION';

// #563: independent Category checkboxes (Filter tab) select any combination of these three real
// roles. An empty set, or a set containing all three, imposes no restriction -- identical to
// 'ANY' -- so those two states are both encoded as "send nothing" (see reactionBucketParams).
export type ReactionCategory = Exclude<ReactionBucket, 'ANY'>;
export const ALL_REACTION_CATEGORIES: readonly ReactionCategory[] = ['REACTANT', 'PRODUCT', 'CONDITION'];

function reactionBucketParams(reactionBuckets: ReadonlySet<ReactionCategory> | undefined): readonly ReactionCategory[] {
  if (!reactionBuckets || reactionBuckets.size === 0) return [];
  if (ALL_REACTION_CATEGORIES.every((category) => reactionBuckets.has(category))) return [];
  return [...reactionBuckets];
}

// #566: the Filter tab's Cycle Sizes / Atom Symbols repeatable rows (#564/#565's backend
// capability). Named/shaped to match the server's own StructuralConstraints record.
export type NumericComparator = 'EQ' | 'LT' | 'LE' | 'GT' | 'GE';

export interface CycleSizeConstraint {
  readonly ringSize: number;
  readonly comparator: NumericComparator;
  readonly count: number;
}

export interface AtomSymbolConstraint {
  readonly symbol: string;
  readonly comparator: NumericComparator;
  readonly count: number;
}

export interface StructuralConstraints {
  readonly cycleSizes: readonly CycleSizeConstraint[];
  readonly atomSymbols: readonly AtomSymbolConstraint[];
}

export const NO_STRUCTURAL_CONSTRAINTS: StructuralConstraints = { cycleSizes: [], atomSymbols: [] };

// The wire format is a repeated, colon-delimited triple per row (`cycleSize=6:EQ:1`), matching
// the same repeated-simple-param convention `reactionBucket` (#563) already uses -- every field
// here is a plain scalar, so a JSON-encoded param would be needless overhead.
function applyStructuralConstraintParams(
  params: URLSearchParams,
  structuralConstraints: StructuralConstraints | undefined
): void {
  for (const c of structuralConstraints?.cycleSizes ?? [])
    params.append('cycleSize', `${c.ringSize}:${c.comparator}:${c.count}`);
  for (const c of structuralConstraints?.atomSymbols ?? [])
    params.append('atomSymbol', `${c.symbol}:${c.comparator}:${c.count}`);
}

export interface MoleculeResult {
  readonly moleculeId: string;
  readonly category: MatchCategory;
  readonly tanimotoScore: number;
  // #519 -- a best-effort PubChem-enriched chemical name, may be null (not every molecule has
  // one, or is indexed for text search at all). ContainerResult.title plays the same "display
  // name" role for saved drawings/reactions/illustrations.
  readonly name: string | null;
  /** #940 -- a substructure hit: its own atom and bond ids the query matched (absent otherwise). */
  readonly matchedAtomIds?: readonly string[] | null;
  readonly matchedBondIds?: readonly string[] | null;
  /** #1060 -- private: only the caller's own tenant holds it (the card says so); public otherwise. */
  readonly visibility?: 'public' | 'private' | null;
  /** #1064 -- your space drew it, but nothing you saved uses it yet. */
  readonly provisional?: boolean;
}

export interface ContainerResult {
  readonly documentId: string;
  readonly documentRevision: number;
  readonly title: string | null;
  readonly sourceMoleculeId: string | null;
  /** A direct match on this document’s own title or visible authored text. */
  readonly textMatch?: boolean;
  readonly category: MatchCategory;
  readonly tanimotoScore: number;
  // #794 -- drawings only: more than its molecule (any annotation), and when last edited.
  readonly hasAnnotations?: boolean;
  readonly updatedAt?: string | null;
  // #811 -- false for someone else's public drawing.
  readonly owned?: boolean;
}

export interface SearchResults {
  readonly molecules: readonly MoleculeResult[];
  readonly drawings: readonly ContainerResult[];
  readonly reactions: readonly ContainerResult[];
  readonly illustrations: readonly ContainerResult[];
}

// #465: a PubChem-sourced hit for a text query the local corpus had zero matches for -- not a
// Molecule record in this database (see the server's PubChemMatch doc comment for the full
// rationale). `pubchemCid` is only populated when the query itself was CID-shaped.
export interface PubChemMatch {
  readonly pubchemCid: string | null;
  readonly label: string;
  readonly smiles: string;
}

// The text-search endpoint's response shape -- identical keys to SearchResults plus one more,
// always-optional field, so every existing SearchResults consumer (normalizeScores below,
// cardsByType, the Text+Drawing intersection) keeps working unchanged on a TextSearchResults too.
export interface TextSearchResults extends SearchResults {
  readonly pubchemFallback: PubChemMatch | null;
  /** #746 -- reactions found by their own words (title, participants' names, notes), not already
   *  among `reactions` (found through a molecule they contain). Results of their own. */
  readonly reactionsByText?: readonly ContainerResult[];
}

// Search validation failures carry a `violations` list the shared default error-message shape
// doesn't have -- kept self-contained (reads the body once) rather than composed with
// defaultErrorMessage, which would need a second, separate read of an already-consumed body.
async function parseSearchErrorMessage(response: Response): Promise<string> {
  const contentType = response.headers.get('content-type') ?? '';
  const fallback = `Molecule search request failed with status ${response.status}.`;
  if (!contentType.includes('json')) return fallback;
  const body = (await response.json().catch(() => undefined)) as
    (ApiProblem & { readonly violations?: unknown }) | undefined;
  const detail = typeof body?.detail === 'string' ? body.detail : undefined;
  const title = typeof body?.title === 'string' ? body.title : undefined;
  const violations = Array.isArray(body?.violations)
    ? body.violations.filter((entry): entry is string => typeof entry === 'string')
    : undefined;
  if (violations && violations.length > 0) return `${detail ?? title ?? fallback} (${violations.join('; ')})`;
  return detail ?? title ?? fallback;
}

export async function searchMolecules(
  query: ChemMoleculeV1Document,
  threshold?: number,
  reactionBuckets?: ReadonlySet<ReactionCategory>,
  structuralConstraints?: StructuralConstraints
): Promise<SearchResults> {
  const params = new URLSearchParams();
  if (threshold !== undefined) params.set('threshold', String(threshold));
  for (const category of reactionBucketParams(reactionBuckets)) params.append('reactionBucket', category);
  applyStructuralConstraintParams(params, structuralConstraints);
  const queryString = params.toString();
  const path = queryString ? `/search/molecules?${queryString}` : '/search/molecules';
  return apiRequest<SearchResults>(
    path,
    'molecule search',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(query)
    },
    { parseErrorMessage: parseSearchErrorMessage }
  );
}

/** #658 (Markush M4): the choice made at one variable position (by its index in the drawing's
 *  `variablePositions`); exactly the fields that apply are set. */
export interface MarkushChoice {
  readonly position: number;
  readonly member?: number | null;
  readonly ringAtomId?: string | null;
  readonly atomicNumber?: number | null;
  readonly count?: number | null;
  /** #660: the group a generic class matched in this molecule, as a formula (`C2H5`). */
  readonly matchedGroup?: string | null;
}

/** #658: SearchResults for a bounded Markush match, plus which structure matched each molecule
 *  (by molecule id) and how many structures the drawing expanded to. */
export interface MarkushSearchResults extends SearchResults {
  readonly matchedBy: Readonly<Record<string, readonly MarkushChoice[]>>;
  readonly structureCount: number;
}

/** #658: bounded Markush match -- every artifact containing a molecule that is, or contains, one of
 *  the drawing's concrete structures. A drawing the server can't search yet (over the cap, generic,
 *  incomplete) fails with its 422 detail as the message. */
export async function searchMarkush(
  markushDrawing: unknown,
  reactionBuckets?: ReadonlySet<ReactionCategory>,
  structuralConstraints?: StructuralConstraints
): Promise<MarkushSearchResults> {
  const params = new URLSearchParams();
  for (const category of reactionBucketParams(reactionBuckets)) params.append('reactionBucket', category);
  applyStructuralConstraintParams(params, structuralConstraints);
  const queryString = params.toString();
  return apiRequest<MarkushSearchResults>(
    queryString ? `/search/molecules/markush?${queryString}` : '/search/molecules/markush',
    'Markush search',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(markushDrawing)
    },
    { parseErrorMessage: parseSearchErrorMessage }
  );
}

// #462's text-search endpoint reuses MatchCategory.IDENTICAL for every hit (an exact/prefix
// identifier match, not a graph-isomorphism claim) and tanimotoScore is Elasticsearch's own,
// unbounded relevance score -- normalized here to [0, 1] (top hit = 1.0) so the existing
// Tanimoto-scaled UI (SearchFilterRail's min-score slider, ResultCardItem's score badge) still
// makes sense for text results without either of them needing to know the score's origin.
// Exported for unit testing -- a pure function, no network dependency, unlike the rest of this
// module (this codebase's convention is to unit test pure logic directly and leave
// network-calling code to e2e coverage; see searchMoleculesByText's own lack of a unit test).
export function normalizeScores(results: SearchResults): SearchResults {
  const scores = [
    ...results.molecules.map((r) => r.tanimotoScore),
    ...results.drawings.map((r) => r.tanimotoScore),
    ...results.reactions.map((r) => r.tanimotoScore),
    ...results.illustrations.map((r) => r.tanimotoScore)
  ];
  const max = Math.max(0, ...scores);
  if (max <= 0) return results;
  const scale = <T extends { readonly tanimotoScore: number }>(items: readonly T[]): T[] =>
    items.map((item) => ({ ...item, tanimotoScore: item.tanimotoScore / max }));
  return {
    molecules: scale(results.molecules),
    drawings: scale(results.drawings),
    reactions: scale(results.reactions),
    illustrations: scale(results.illustrations)
  };
}

export async function searchMoleculesByText(
  query: string,
  reactionBuckets?: ReadonlySet<ReactionCategory>,
  structuralConstraints?: StructuralConstraints
): Promise<TextSearchResults> {
  const params = new URLSearchParams({ q: query });
  for (const category of reactionBucketParams(reactionBuckets)) params.append('reactionBucket', category);
  applyStructuralConstraintParams(params, structuralConstraints);
  const results = await apiRequest<TextSearchResults>(`/search/molecules/text?${params.toString()}`, 'molecule search');
  // normalizeScores only knows about the six SearchResults buckets -- pubchemFallback carries no
  // score to normalize, so it's reattached rather than threaded through that function's signature.
  return { ...normalizeScores(results), pubchemFallback: results.pubchemFallback };
}
